import React, { useState, useEffect, useCallback } from 'react';
import { IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, Stack, CircularProgress, Tooltip, Snackbar, Alert } from '@mui/material';
import { Alarm as AlarmIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiRequest } from '../utils/api';

const CustomReminderDialog = () => {
  const [open, setOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', scheduledAt: '' });
  const [deleting, setDeleting] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiRequest('/api/custom-reminders');
      setReminders(Array.isArray(r.data) ? r.data : []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchReminders(); }, [open, fetchReminders]);

  const handleAdd = async () => {
    setScheduling(true);
    try {
      await apiRequest('/api/custom-reminders', { method: 'POST', body: JSON.stringify({ title: form.title, message: form.message, scheduledAt: new Date(form.scheduledAt).toISOString() }) });
      setSnackbar({ severity: 'success', text: 'Reminder scheduled' });
      setAddOpen(false); setForm({ title: '', message: '', scheduledAt: '' }); fetchReminders();
    } catch (e) { setSnackbar({ severity: 'error', text: `Failed: ${e.message}` }); } finally { setScheduling(false); }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try { await apiRequest(`/api/custom-reminders/${id}`, { method: 'DELETE' }); setSnackbar({ severity: 'success', text: 'Deleted' }); fetchReminders(); }
    catch (e) { setSnackbar({ severity: 'error', text: `Failed: ${e.message}` }); } finally { setDeleting(null); }
  };

  return (
    <>
      <Tooltip title="Custom Reminders">
        <IconButton color="inherit" size="small" onClick={() => setOpen(true)} aria-label="Custom Reminders">
          <AlarmIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Custom Reminders
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add</Button>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : reminders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No reminders. Click "Add" to create one.</Typography>
          ) : (
            <Stack spacing={1}>
              {reminders.map((r) => (
                <Box key={r.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Box>
                    <Typography variant="body1" fontWeight="bold">{r.title}</Typography>
                    {r.message && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{r.message}</Typography>}
                    <Typography variant="caption" color={r.is_sent ? 'success.main' : 'warning.main'}>{r.is_sent ? 'Sent' : new Date(r.scheduled_at).toLocaleString()}</Typography>
                  </Box>
                  <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} disabled={deleting === r.id}>
                    {deleting === r.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Close</Button></DialogActions>
      </Dialog>
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Custom Reminder</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Check SPX before close" />
            <TextField fullWidth size="small" label="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} multiline rows={3} placeholder="Enter the reminder text..." />
            <TextField fullWidth size="small" type="datetime-local" label="Scheduled Time" value={form.scheduledAt} onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })} InputLabelProps={{ shrink: true }} />
            <Typography variant="caption" color="text.secondary">The reminder will be sent to all enabled notification providers at the scheduled time.</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!form.title || !form.scheduledAt || scheduling} startIcon={scheduling ? <CircularProgress size={16} /> : null}>Schedule</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar(null)} severity={snackbar?.severity || 'info'} sx={{ width: '100%' }}>{snackbar?.text}</Alert>
      </Snackbar>
    </>
  );
};

export default CustomReminderDialog;
